import { axiosBaseQuery } from '@client';
import { ExpenseEvaluationFormulaResponse } from '@module/expense-evaluation/model/ExpenseEvaluationFormulaResponse';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import {
  ClarificationsDraftResponse,
  ClarificationsRequestDto,
  FormulaAnalyserFeedbackForm
} from './models';
import { retry } from '@reduxjs/toolkit/query';
import { toaster } from '@common';

export type FetchEvaluationDiscrepanciesTrigger = ReturnType<
  typeof useLazyFetchEvaluationDiscrepanciesQuery
>[0];

const MAX_RETRIES = 2;

export const isGatewayTimeoutQueryError = (error: unknown): boolean => {
  if (typeof error !== 'object' || error === null || !('status' in error)) {
    return false;
  }
  const status = (error as { status?: number }).status;
  return status === 504 || status === undefined;
};

const baseQueryWithRetry = retry(axiosBaseQuery(), {
  retryCondition: (error, _, { attempt, extraOptions }) => {
    const maxRetries =
      (extraOptions as { maxRetries?: number }).maxRetries ?? 0;

    if (attempt > maxRetries) {
      return false;
    }

    return isGatewayTimeoutQueryError(error);
  },
  backoff: async (attempt) => {
    if (attempt <= MAX_RETRIES) {
      toaster.info('HL AI is working, please wait.');
    }
    await new Promise((resolve) => setTimeout(resolve, 600 * 2 ** attempt));
  }
});

export type AiNegotiatorAsyncTrigger = ReturnType<
  typeof useAiNegotiatorMutation
>[0];

export type RunAiNegotiatorTrigger = ReturnType<
  typeof useRunAiNegotiatorMutation
>[0];

export type RunHusbandryNegotiatorTrigger = ReturnType<
  typeof useRunHusbandryNegotiatorMutation
>[0];

export type AiNegotiatorAcceptedResponse = {
  status: 'accepted';
};

export type AiNegotiatorRunResponse = {
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  uuid: string;
  results: {
    pda: unknown | null;
    ranges: unknown | null;
    comparison: unknown | null;
  };
};

export type HusbandryNegotiatorRunResponse = {
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  uuid: string;
  results: {
    comparison: unknown | null;
  };
};

export const expenseEvaluationFormulaApi = createApi({
  baseQuery: baseQueryWithRetry,
  reducerPath: 'expense-evaluation-formula_api',
  endpoints: (build) => ({
    fetchExpenseEvaluationFormula: build.query<
      ExpenseEvaluationFormulaResponse,
      {
        evaluationId: string;
        expenseEvaluationId: number;
        exchangeRate?: number;
        forceClearCache: boolean;
      }
    >({
      extraOptions: { maxRetries: MAX_RETRIES },
      query: ({
        evaluationId,
        expenseEvaluationId,
        exchangeRate,
        forceClearCache
      }) => ({
        url: `/secured/evaluation/${evaluationId}/expense/${expenseEvaluationId}/analyze`,
        method: 'GET',
        params: {
          exchangeRate: !!exchangeRate ? String(exchangeRate) : undefined,
          forceClearCache: String(forceClearCache)
        }
      })
    }),
    fetchPortExpenseEstimationLastChecked: build.query<
      string,
      { estimationId: string }
    >({
      query: ({ estimationId }) => ({
        url: `/secured/port-expense-estimation/${estimationId}/last-checked`,
        method: 'GET'
      })
    }),
    fetchProformaExpenseFormula: build.query<
      ExpenseEvaluationFormulaResponse,
      {
        proformaId: string;
        proformaExpenseId: number;
        exchangeRate?: number;
        estimationResult?: number;
        forceClearCache: boolean;
      }
    >({
      extraOptions: { maxRetries: MAX_RETRIES },
      query: ({
        proformaId,
        proformaExpenseId,
        exchangeRate,
        estimationResult,
        forceClearCache
      }) => ({
        url: `/secured/proforma/${proformaId}/expense/${proformaExpenseId}/analyze`,
        method: 'GET',
        params: {
          exchangeRate: !!exchangeRate ? String(exchangeRate) : undefined,
          estimationResult:
            estimationResult !== undefined
              ? String(estimationResult)
              : undefined,
          forceClearCache: String(forceClearCache)
        }
      })
    }),
    rateFormulaAnalyzerFeedback: build.mutation<
      void,
      FormulaAnalyserFeedbackForm
    >({
      query: (data) => ({
        url: `/secured/formula/analyzer/feedback`,
        method: 'POST',
        data
      })
    }),
    fetchEvaluationDiscrepancies: build.query<
      ExpenseEvaluationFormulaResponse[],
      { evaluationId: string }
    >({
      extraOptions: { maxRetries: MAX_RETRIES },
      query: ({ evaluationId }) => ({
        url: `/secured/evaluation/${evaluationId}/analyze/discrepancies`,
        method: 'GET'
      })
    }),
    sendClarificationsRequest: build.mutation<
      void,
      { evaluationId: string; data: ClarificationsRequestDto }
    >({
      query: ({ evaluationId, data }) => ({
        url: `/secured/evaluation/${evaluationId}/clarifications-request/send`,
        method: 'POST',
        data
      })
    }),
    getClarificationsRequestDraft: build.mutation<
      ClarificationsDraftResponse,
      { evaluationId: number }
    >({
      query: ({ evaluationId }) => ({
        url: `/secured/evaluation/${evaluationId}/clarifications-request/draft`,
        method: 'GET'
      })
    }),
    aiNegotiator: build.mutation<
      AiNegotiatorAcceptedResponse,
      { proforma_id: number; uuid: string }
    >({
      query: ({ proforma_id, uuid }) => ({
        url: '/secured/ai-negotiator',
        method: 'POST',
        data: { proformaId: proforma_id, uuid }
      })
    }),
    runAiNegotiator: build.mutation<
      AiNegotiatorRunResponse,
      { evaluationId: number; uuid?: string }
    >({
      query: ({ evaluationId, uuid }) => ({
        url: `/secured/ai-negotiator/evaluation/${evaluationId}`,
        method: 'POST',
        data: uuid ? { uuid } : {}
      })
    }),
    runHusbandryNegotiator: build.mutation<
      HusbandryNegotiatorRunResponse,
      { evaluationId: number; uuid?: string }
    >({
      query: ({ evaluationId, uuid }) => ({
        url: `/secured/husbandry-negotiator/evaluation/${evaluationId}`,
        method: 'POST',
        data: uuid ? { uuid } : {}
      })
    })
  })
});

export const {
  useLazyFetchExpenseEvaluationFormulaQuery,
  useLazyFetchProformaExpenseFormulaQuery,
  useRateFormulaAnalyzerFeedbackMutation,
  useLazyFetchEvaluationDiscrepanciesQuery,
  useSendClarificationsRequestMutation,
  useGetClarificationsRequestDraftMutation,
  useAiNegotiatorMutation,
  useRunAiNegotiatorMutation,
  useRunHusbandryNegotiatorMutation
} = expenseEvaluationFormulaApi;
